import { useState, useEffect, useRef, useMemo } from 'react';
import { Time } from '@/ui/Time';
import { Avatar, Button, Flex, Icon, Link, Tooltip } from '@chakra-ui/react';
import { parseNoteToFormat, parseNoteToHtml } from './notesHelpers';
import { SanitizedText } from '@/ui/SanitizedDom';
import { NotesAccessMenu } from './NotesAccessmenu';
import { TaggedUsers } from './TaggedUsers';
import { Edit03 } from '@kula-ai/icons/dual-tone/Edit03';
import { Atom01 } from '@kula-ai/icons/dual-tone/Atom01';
import { NotesEditor } from '@/components/editor/NotesEditor';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { useParams } from 'react-router-dom';
import { KText } from '@/ui-library/components/text';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';

export const Note = ({
  note,
  handleNoteAccessChange = (_data) => {},
  handleNoteUpdate = (_data) => {},
  isUpdating = false,
  type,
  currNoteId = null,
  setCurrNoteId = () => {},
  reference = '',
  transferInfo = null,
}) => {
  const {
    created_by,
    updated_at,
    body,
    tagged_users,
    shared,
    private: isPrivate,
    association,
    associated_entity,
    creator_type,
  } = note || {};
  const modifiedBody = parseNoteToHtml(body || '', tagged_users || []);
  const [noteBody, setBody] = useState(modifiedBody);
  const [editMode, setEditMode] = useState(false);
  const [isEnterPressed, setEnterPressed] = useState(false);
  const currUser = useSelector(selectCurrUser);
  const { interviewId } = useParams();
  const containerRef = useRef(null);

  const handleSubmit = async () => {
    setCurrNoteId(note?.id);
    setEnterPressed(false);
    const { note: noteInTextArea, taggedUsers } = parseNoteToFormat(
      noteBody || ''
    );
    if (noteInTextArea?.trim() === '') return;
    const payload = {
      body: noteInTextArea,
      private: isPrivate,
      tagged_user_ids: taggedUsers,
    };
    handleNoteUpdate({ payload, note, setEditMode });
  };

  const onAccessChange = async (role) => {
    const payload = {
      private: role === 'private',
      shared: role === 'shared' ? true : false,
      body,
      tagged_user_ids: tagged_users?.map((item) => item?.id),
    };
    handleNoteAccessChange({ payload, note });
  };

  const getNoteContainerStyles = useMemo(() => {
    if (reference === 'application_revamp') {
      return {
        border: 'none',
        borderRadius: '0px',
        marginBottom: '30px',
      };
    }
    return {
      border: '1px solid',
      borderColor: 'border-color',
      borderRadius: 'sm',
      p: '16px',
    };
  }, [reference]);

  useEffect(() => {
    if (!isEnterPressed) return;
    handleSubmit();
  }, [isEnterPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (editMode) {
    return (
      <Flex
        border="1px solid"
        borderColor="border-color"
        borderRadius="sm"
        p="16px"
        gap="16px"
        flexDir={'column'}
      >
        <Flex w="100%" flexDir="column" p="10px" pb="0px">
          <NotesEditor
            onUpdate={(html) => {
              setBody(html);
              setEnterPressed(false);
            }}
            onEnter={() => setEnterPressed((prev) => !prev)}
            isEnterPressed={isEnterPressed}
            defaultValue={noteBody}
            height="100px"
            isUpdate={true}
            setInitialFocus={true}
          />
        </Flex>
        <Flex gap="10px" ml="auto">
          <Button
            variant="secondary"
            onClick={() => setEditMode(false)}
            data-test-id="secondary-cta-button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => setEnterPressed(true)}
            isLoading={currNoteId === note?.id && isUpdating}
            data-test-id="primary-cta-button"
          >
            Save
          </Button>
        </Flex>
      </Flex>
    );
  }

  const linksConfig = [
    {
      id: 'job',
      label: 'Job',
      link: `/jobs/${association?.job?.id}?tab=candidates&appId=${association?.id}&appTab=notes`,
      name: association?.job?.name || '',
      show: type === 'contact' && association?.type === 'AtsApplication',
    },
    {
      id: 'interview',
      label: 'Interview',
      link: `/interviews/${associated_entity?.id}?section=notes`,
      name: associated_entity?.name,
      show: associated_entity?.id && +interviewId !== associated_entity?.id,
    },
  ];

  const renderLinks = () => {
    return (
      linksConfig?.filter((config) => config?.show)?.length > 0 && (
        <Flex gap="6px">
          {linksConfig?.map((config) => {
            const { id, label, link, show, name } = config;

            if (!show) return null;

            return (
              <Flex
                p="4px 8px"
                borderRadius="xs"
                bg="ats-tertiary"
                alignItems="center"
                gap="6px"
                key={id}
              >
                <KText size="sm-medium">{label}:</KText>
                <Link
                  href={link}
                  isExternal
                  display="flex"
                  gap="4px"
                  alignItems="center"
                >
                  <TruncatedTextWithTooltip
                    text={name}
                    maxW="120px"
                    fontSize="sm-regular"
                  />
                  <Icon as={HiArrowTopRightOnSquare} color="ats-primary" />
                </Link>
              </Flex>
            );
          })}
        </Flex>
      )
    );
  };

  if (creator_type === 'contact') return null;

  return (
    <Flex {...getNoteContainerStyles} flexDir="column" gap="7px">
      <Flex
        gap="12px"
        alignItems="center"
        justifyContent="space-between"
        maxWidth="100%"
      >
        <Flex
          gap="6px"
          alignItems="center"
          maxWidth={
            reference === 'application_revamp'
              ? `calc(100% - 65px)`
              : `calc(100% - ${
                  (containerRef.current?.offsetWidth || 150) + 18
                }px)`
          }
        >
          {created_by?.is_ai_coordinator ? (
            <Flex
              w="18px"
              h="18px"
              borderRadius="4px"
              bg="secondary-950"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon as={Atom01} w="12px" h="12px" color="base-white" />
            </Flex>
          ) : (
            <Avatar
              src={created_by?.avatar || ''}
              name={created_by?.name || ''}
              w="18px"
              h="18px"
              borderRadius="6px"
              className="avatar-xs"
            />
          )}
          <TruncatedTextWithTooltip
            text={created_by?.name || ''}
            size="sm-medium"
            color="primary-900"
          />
          {note?.is_transferred && (
            <TransferredBadge transferInfo={transferInfo} />
          )}
        </Flex>
        <Flex
          alignItems="center"
          gap={reference === 'application_revamp' ? '8px' : '12px'}
          ref={containerRef}
        >
          {created_by && currUser?.id === created_by?.id && (
            <Tooltip label="Edit" placement="top">
              <Flex>
                <Icon
                  as={Edit03}
                  w="16px"
                  h="16px"
                  cursor="pointer"
                  onClick={() => setEditMode(true)}
                  data-test-id="edit-note-button"
                />
              </Flex>
            </Tooltip>
          )}
          <NotesAccessMenu
            selected={isPrivate ? 'private' : shared ? 'shared' : 'public'}
            setSelected={onAccessChange}
            isEditable={created_by && currUser?.id === created_by?.id}
            isShared={type === 'interview'}
            reference={reference}
          />
          {tagged_users?.length > 0 && (
            <TaggedUsers taggedUsers={tagged_users} />
          )}
        </Flex>
      </Flex>
      <Flex alignItems="flex-start">
        <SanitizedText
          className="ats-note"
          html={modifiedBody}
          flex="1"
          overflowWrap="anywhere"
          wordBreak="break-word"
          lineHeight="20px"
        />
      </Flex>
      <Flex justifyContent="space-between">
        <Flex gap="4px" fontSize={'12px'}>
          <KText size="xs-regular" color="secondary-500">
            Last modified
          </KText>
          <Time time={updated_at} size="xs-regular" color="secondary-500" />
        </Flex>
        {type !== 'quick_app_review' && renderLinks()}
      </Flex>
      {type === 'quick_app_review' && renderLinks()}
    </Flex>
  );
};
